import React, {lazy, Suspense, useEffect, useState} from "react";
import clsx from "clsx";

import "./assets/scss/Dashboard.scss";
import BasicButton from "../components/BasicButton";
import Block from "../components/Block";
import LoadingAnimation from "../components/LoadingAnimation";

import MeterSettings from "./modals/MeterSettingsModal";
import {meterTypeComponentMapping, pdashColors} from "./Dashboard";

import {
    ATTR_OBJ_ID,
    ATTR_OBJ_TYPE,
    currentTimeMs,
    FEAT_SHOW_TABLE,
    featureEnabled, getActiveSubProjectName, getPdashProjectSettings,
    hasPermissionTo,
    OBJ_STATE_TO_DEL, OBJ_TYPE_METER_GANTT, OBJ_TYPE_METER_STATUS,
    QUERY_DASHBOARD_METERS,
    resetLongQueryPollPeriod,
    savePdashMeter,
    startLongQuery,
    stopLongQuery,
} from "../prodect/ProsConnect";
import * as prosConnect from "../prodect/ProsConnect";
import {USE_DEVELOPMENT_FEATURES} from "../config";
import {cleanFilename} from "../prodect/ProsCommonUi";

export default function Meter(props) {
    const projectId = props.projectId
    const meterId = props.meterSettings.obj_id
    const meterType = props.meterSettings.obj_type
    const { meterSize } = props

    const [isEditing, setIsEditing] = useState(false)
    const [meterSettings, setMeterSettings] = useState(props.meterSettings)
    const [meterData, setMeterData] = useState({})
    const [project_settings, setProject_settings] = useState(undefined)

    useEffect(() => {
        startLongQuery(meterId, QUERY_DASHBOARD_METERS, meterId, (response, isFresh, lastUpdated) => {
            let newData = response.data
            newData.isFresh = isFresh
            newData.lastUpdated = lastUpdated
            setMeterData(newData)
        }, {
            project_id: projectId,
            obj_type: meterType,
            debug: prosConnect.isSuperUserModeInUse() || USE_DEVELOPMENT_FEATURES
        })

        return () => stopLongQuery(meterId)
    }, [projectId, meterId, meterType, props.meterSettings])

    function isLoading() {
        return (!meterData)
    }

    function isFresh() {
        return meterData && meterData.isFresh
    }

    function lastUpdated() {
        return meterData ? meterData.lastUpdated : 0
    }

    function timeSinceUpdate(milliseconds) {
        var seconds = Math.floor(milliseconds / 1000)
        var minutes = Math.floor(seconds / 60)
        var hours = Math.floor(minutes / 60)
        var days = Math.floor(hours / 24)
        var weeks = Math.floor(days / 7)
        if (weeks > 0) {
            return 'yli viikko'
        } else if (days > 0) {
            return days + ' päivä' + (days > 1 ? 'ä' : '')
        } else if (hours > 0) {
            return hours + ' h'
        } else if (minutes > 0) {
            return minutes + ' m'
        } else {
            return '< 1min'
        }
    }

    function changeSize() {
        const newSize = (meterSize + 1) % 3
        props.onSizeChange(newSize)
    }

    const Component = meterType in meterTypeComponentMapping
        ? lazy(meterTypeComponentMapping[meterType])
        : () => <p>Tuntematon kuvaaja: {meterType}</p>

    function renderFilterButton() {
        if (!featureEnabled(FEAT_SHOW_TABLE) || !meterData || !props.editTableLinkSettings) {
            return null
        }

        const countFiltered = meterData.data_obs_count
        const countAll = meterData.project_info?.obs_count
        const isFiltered = countFiltered !== countAll

        const tooltip = isFiltered
            ? `Suodatus käytössä (${countFiltered}/${countAll})`
            : 'Suodata'

        return <BasicButton
            icon={"funnel"}
            hilite={isFiltered}
            tooltip={[tooltip, "tooltip-left_bottom"]}
            action={() => props.editTableLinkSettings(projectId, meterId, meterSettings)}
            tag={isFiltered ? (countFiltered > 999 ? true : countFiltered)  : null}
        />
    }

    function renderBlockButtons(){
        return <div className={"btn-set_right flex-gap-sm"}>
            <span className={"icon-text-set-xs text-sm color-gray-60"}
                  data-tooltip={"Viimeksi päivitetty"}>
                <i className={"ui-icon ui-icon-sm ui-icon-history"}/>
                {timeSinceUpdate(currentTimeMs() - lastUpdated())}
            </span>
            {!isFresh() && <LoadingAnimation type="arrows_mask" bg="var(--color-bg-content)" />}
            <BasicButton
                icon={(meterSize > 1 ? "minimize" : "expand")}
                tooltip={[(meterSize > 1 ? "Pienennä" : "Suurenna") + " kuvaaja", "tooltip-left_bottom"]}
                action={()=> changeSize()}
            />
            {renderFilterButton()}
            {hasPermissionTo('editDashboard') && (
                <BasicButton
                    icon="ellipsis-h"
                    tooltip={["Näyttöasetukset", "tooltip-left_bottom"]}
                    action={async () => {
                        if ( ! project_settings && [OBJ_TYPE_METER_STATUS, OBJ_TYPE_METER_GANTT].indexOf(meterType) >= 0) {
                            const proj_settings = await getPdashProjectSettings(projectId)
                            setProject_settings(proj_settings)
                        }
                        setIsEditing(true)
                    }}
                />
            )}
        </div>
    }

    return <Block
            title={meterSettings.text}
            subtitle={meterSettings.project_settings.text}
            buttons={renderBlockButtons()}
            className={clsx({
                'size-1': meterSize === 0,
                'size-2': meterSize === 1,
                'size-3': meterSize === 2,
            })}
        >
        {isEditing && (
            <MeterSettings
                project_settings={project_settings}
                projectId={projectId}
                meterType={meterType}
                meterSettings={meterSettings}
                onSave={async newSettings => {
                    setMeterData({})
                    await savePdashMeter(projectId, newSettings)
                    resetLongQueryPollPeriod(meterId)
                    setIsEditing(false)
                    setMeterSettings(newSettings)
                }}
                onClose={() => {
                    setIsEditing(false)
                }}
                onDelete={async () => {
                    await savePdashMeter(projectId, {
                        ...meterSettings,
                        obj_state: OBJ_STATE_TO_DEL,
                    })

                    props.onUpdateRequested()
                }}
                meterData={meterData}
            />
        )}
        {/*Todo: PETJA, missing some logic here. When it is actually creating a new meter, add .is-saving class only then*/}
        <section className={isLoading() ? "is-saving" : null}>
            <Suspense fallback={<div>Ladataan...</div>}>
                <Component
                    meterData={meterData}
                    meterSettings={meterSettings}
                    isEditing={isEditing}
                    meterSize={meterSize}
                />
            </Suspense>
        </section>
    </Block>
}

export function MeterDownloadButton(props){

    function triggerDownload(imgURI, filename) {
        const a = document.createElement('a');
        a.download = filename
        a.target = '_blank'
        a.href = imgURI
        // set `bubbles` to false here. or just `a.click()` if you don't care about bubbling
        a.dispatchEvent(new MouseEvent('click', {
            view: window,
            bubbles: false,
            cancelable: true
        }))
    }

    function saveSVG(svgEl, name){
        svgEl.setAttribute("style", "background-color:" + pdashColors['color-bg-content'])
        const svgString = (new XMLSerializer()).serializeToString(svgEl);
        const svgBlob = new Blob([svgString], {
            type: 'image/svg+xml;charset=utf-8'
        })
        const DOMURL = window.URL || window.webkitURL || window
        const url = DOMURL.createObjectURL(svgBlob)
        const image = new Image()
        image.width = svgEl.width.baseVal.value
        image.height = svgEl.height.baseVal.value
        image.src = url
        image.onload = function () {
            const canvas = document.createElement('canvas')//getElementById('canvas');
            canvas.width = image.width
            canvas.height = image.height
            const ctx = canvas.getContext('2d');
            ctx.drawImage(image, 0, 0);
            DOMURL.revokeObjectURL(url);

            const imgURI = canvas
                .toDataURL('image/png')
                .replace('image/png', 'image/octet-stream');
            triggerDownload(imgURI, name)
        }
    }
    // function convertToPlain(html){
    //     var tempDivElement = document.createElement("div");
    //     tempDivElement.innerHTML = html
    //     // return tempDivElement.textContent || tempDivElement.innerText || "";
    //     return tempDivElement.innerText
    // }
    // function savePNG(htmlEl, name) {
    //     const {body} = document
    //     const canvas = document.createElement('canvas')
    //     const ctx = canvas.getContext('2d')
    //     canvas.width = htmlEl.clientWidth
    //     canvas.height = htmlEl.clientHeight
    //     const data = '<svg xmlns="http://www.w3.org/2000/svg" width="' + htmlEl.clientWidth + '" height="' + htmlEl.clientHeight + '">' +
    //         '<foreignObject width="100%" height="100%">' +
    //         convertToPlain(htmlEl.outerHTML) +
    //         '</foreignObject>' +
    //     '</svg>';
    //     const tempImg = document.createElement('img')
    //     tempImg.addEventListener('load', onTempImageLoad)
    //     tempImg.src = 'data:image/svg+xml,' + encodeURIComponent(data)
    //     const targetImg = document.createElement('img')
    //     body.appendChild(targetImg)
    //     function onTempImageLoad(e){
    //         ctx.drawImage(e.target, 0, 0)
    //         targetImg.src = canvas.toDataURL()
    //     }
    // }

    return <BasicButton
        icon={"download"}
        action={()=> {
            saveSVG(props.target.current.children[0], 'prodect_' + cleanFilename(getActiveSubProjectName()) + '_' + props.meterSettings[ATTR_OBJ_TYPE] + '_' + props.meterSettings[ATTR_OBJ_ID] + '.png')
        }}
        size={"md"}
        tooltip={["Tallenna kuvana", "tooltip-left"]}
    />
}